/**
 * Marketing / product landing page for Family Trees & Eternal Memorials.
 *
 * Route: /family-and-memorials
 *
 * Links from the top of `/family` and `/celebrate` point here so a
 * visitor deciding whether to sign up can read the full narrative
 * before committing.
 *
 * Visual identity intentionally distinct from the two feature pages —
 * warm cream editorial with terracotta / moss accents, generous
 * whitespace, asymmetric section layouts, subtle grain texture.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  TreeDeciduous, Flower2, Users, Sparkles, ShieldCheck, ImagePlus,
  MessageCircleHeart, Bell, HeartHandshake, Globe, ArrowUpRight,
  ScrollText, Calendar, Fingerprint, BookHeart, Mail, Check, Loader2,
} from "lucide-react";

const CREAM   = "#FDFBF7";
const INK     = "#2D2A26";
const SOFT    = "#5C5852";
const TERRA   = "#B25D38";
const MOSS    = "#5C6B52";
const ROSE    = "#B23A4E";
const SLATE   = "#3D2E5A";

export default function FamilyMemorialsMarketing() {
  return (
    <div
      data-testid="family-memorials-marketing"
      className="min-h-screen"
      style={{ background: CREAM, color: INK }}
    >
      {/* ================= HERO ================= */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div>
            <div
              className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full"
              style={{ background: "#F3EFE7", color: TERRA }}
            >
              <TreeDeciduous size={13} /> Family Trees & Eternal Memorials
            </div>
            <h1
              className="font-display font-black leading-[0.95] mt-6 text-5xl sm:text-6xl lg:text-7xl"
              style={{ color: INK }}
            >
              Preserve Your <span style={{ color: TERRA, fontStyle: "italic" }}>Family Legacy</span>.
            </h1>
            <p className="mt-6 text-lg lg:text-xl max-w-2xl leading-relaxed" style={{ color: SOFT }}>
              Build your family tree, reconnect with relatives around the world, create lasting memorials for loved ones, and preserve your family's history for future generations.
            </p>
            <p className="mt-4 text-base max-w-2xl leading-relaxed" style={{ color: SOFT }}>
              Whether your family lives in <b style={{ color: INK }}>Kerala</b>, across <b style={{ color: INK }}>India</b>, or throughout the <b style={{ color: INK }}>global diaspora</b>, Social&nbsp;Pulse helps bring every generation together in one secure and beautiful place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/family"
                data-testid="marketing-cta-family"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium shadow-md hover:shadow-lg transition-shadow"
                style={{ background: TERRA, color: "white" }}
              >
                <TreeDeciduous className="w-4 h-4" /> Start Your Family Tree
              </Link>
              <Link
                to="/celebrate"
                data-testid="marketing-cta-memorial"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border-2 hover:bg-white/60 transition-colors"
                style={{ borderColor: MOSS, color: MOSS }}
              >
                <Flower2 className="w-4 h-4" /> Create a Memorial
              </Link>
            </div>
            <div
              className="mt-8 inline-flex items-center gap-2 text-[13px] italic"
              style={{ color: SLATE }}
            >
              <Fingerprint size={14} /> Made for Indian families &mdash; wherever they call home.
            </div>
          </div>

          {/* Decorative illustrative panel */}
          <div className="relative hidden lg:block" aria-hidden>
            <div
              className="rounded-[2rem] p-8 shadow-lg"
              style={{ background: "linear-gradient(155deg,#F3EFE7 0%,#EAE1D3 100%)" }}
            >
              <TreeMotif />
              <div className="mt-6 space-y-3">
                <MiniRow icon={<Users className="w-4 h-4" />} label="7 generations mapped" color={TERRA} />
                <MiniRow icon={<Flower2 className="w-4 h-4" />} label="42 lives celebrated" color={ROSE} />
                <MiniRow icon={<ImagePlus className="w-4 h-4" />} label="380 photos preserved" color={MOSS} />
                <MiniRow icon={<MessageCircleHeart className="w-4 h-4" />} label="128 tributes shared" color={SLATE} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= DIVIDER ================= */}
      <SectionDivider />

      {/* ================= FAMILY TREE ================= */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
          <div className="lg:sticky lg:top-24">
            <div
              className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full"
              style={{ background: "#EDF2E9", color: MOSS }}
            >
              🌳 Family Tree
            </div>
            <h2 className="font-display font-black leading-[1.02] mt-5 text-4xl sm:text-5xl">
              Generations, <span style={{ color: MOSS, fontStyle: "italic" }}>side by side</span>.
            </h2>
            <p className="mt-5 text-base leading-relaxed" style={{ color: SOFT }}>
              Every branch tells a story. Every name represents a life. Visualize your family's journey across generations with an elegant, interactive family tree that's easy to build and enjoyable to explore.
            </p>
            <Link
              to="/family"
              data-testid="marketing-family-start"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium border-b-2 pb-1 hover:opacity-80 transition-opacity"
              style={{ color: MOSS, borderColor: MOSS }}
            >
              Explore Family Trees <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <FeatureCard
              icon={<TreeDeciduous />}
              accent={MOSS}
              title="Grow Your Tree"
              body="Add parents, grandparents, children, siblings, cousins, aunts, uncles, and future generations. Watch your family history unfold with beautiful visual timelines and relationship mapping."
              testid="feat-grow-tree"
            />
            <FeatureCard
              icon={<Users />}
              accent={TERRA}
              title="Reconnect with Family"
              body="Invite relatives from anywhere in the world to collaborate. Together you can add names, photographs, stories, documents, and memories — ensuring every generation contributes to your shared history."
              testid="feat-reconnect"
            />
            <FeatureCard
              icon={<Globe />}
              accent={SLATE}
              title="Discover Your Roots"
              body="Our intelligent matching technology can help identify potential family connections, ancestral villages, and relatives across the Indian diaspora — making it easier to rediscover long-lost branches of your family."
              testid="feat-roots"
            />
            <FeatureCard
              icon={<ScrollText />}
              accent={ROSE}
              title="Preserve Family Heritage"
              body="Upload treasured photographs, handwritten letters, certificates, passports, property records, recipes, migration stories, audio recordings, and family traditions. Create a permanent digital archive that future generations will cherish."
              testid="feat-heritage"
            />
            <FeatureCard
              icon={<Sparkles />}
              accent={TERRA}
              title="AI-Powered Storytelling"
              body="Transform simple dates and photographs into beautifully written life stories. AI helps create biographies, family histories, timelines, and narratives that celebrate every person's unique journey."
              testid="feat-ai-story"
            />
            <FeatureCard
              icon={<ShieldCheck />}
              accent={MOSS}
              title="Private & Secure"
              body="Your family's information belongs to you. Control who can view, edit, or contribute to your family tree with flexible privacy settings designed to protect every generation."
              testid="feat-private"
            />
          </div>
        </div>
      </section>

      {/* ================= DIVIDER ================= */}
      <SectionDivider flip />

      {/* ================= MEMORIALS ================= */}
      <section
        className="py-20"
        style={{ background: "linear-gradient(180deg, #FBF6EE 0%, #F3E8F3 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12 items-start">
            <div className="grid sm:grid-cols-2 gap-4">
              <FeatureCard
                icon={<BookHeart />}
                accent={ROSE}
                title="A Living Tribute"
                body="Share biographies, photographs, videos, achievements, favorite quotes, milestones, and cherished memories in one thoughtfully designed memorial page that family and friends can visit from anywhere in the world."
                testid="feat-tribute"
              />
              <FeatureCard
                icon={<MessageCircleHeart />}
                accent={SLATE}
                title="Gather Memories Together"
                body="Invite relatives and friends to contribute stories, condolences, photographs, and personal reflections. Every contribution helps create a richer, more complete portrait of a life that touched many others."
                testid="feat-gather"
              />
              <FeatureCard
                icon={<Calendar />}
                accent={TERRA}
                title="Memorial Timeline"
                body="Document the important moments that shaped a person's life — from birth and education to marriage, career, family milestones, travels, achievements, and community service."
                testid="feat-timeline"
              />
              <FeatureCard
                icon={<Users />}
                accent={MOSS}
                title="Family Connections"
                body="Every memorial is connected to the family tree, allowing future generations to understand relationships, ancestry, and the people who came before them."
                testid="feat-connections"
              />
              <FeatureCard
                icon={<Bell />}
                accent={ROSE}
                title="Never Forgotten"
                body="Keep birthdays, anniversaries, remembrance days, and family milestones alive with optional reminders and memorial events that bring families together to honor those they love."
                testid="feat-remember"
              />
              <FeatureCard
                icon={<ImagePlus />}
                accent={SLATE}
                title="Preserve Their Legacy"
                body="Store photographs, letters, certificates, awards, military records, family recipes, voice recordings, and treasured keepsakes in a permanent digital archive that can be safely passed down through generations."
                testid="feat-preserve"
              />
              <FeatureCard
                icon={<Sparkles />}
                accent={TERRA}
                title="AI Memory Companion"
                body="AI can help organize memories, write biographies, summarize life stories, restore old photographs, generate timelines, and transform scattered information into a lasting tribute."
                testid="feat-ai-memory"
                span={2}
              />
            </div>

            <div className="lg:sticky lg:top-24 order-first lg:order-last">
              <div
                className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full"
                style={{ background: "#F3E1E6", color: ROSE }}
              >
                🕊 Eternal Memorials
              </div>
              <h2 className="font-display font-black leading-[1.02] mt-5 text-4xl sm:text-5xl">
                Celebrate a life <span style={{ color: ROSE, fontStyle: "italic" }}>well lived</span>.
              </h2>
              <p className="mt-5 text-base leading-relaxed" style={{ color: SOFT }}>
                Every life deserves to be remembered. Create a beautiful online memorial that honors the memories, accomplishments, values, and legacy of your loved ones.
              </p>
              <Link
                to="/celebrate"
                data-testid="marketing-memorial-start"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium border-b-2 pb-1 hover:opacity-80 transition-opacity"
                style={{ color: ROSE, borderColor: ROSE }}
              >
                Explore Celebrate a Life <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY FAMILIES CHOOSE US ================= */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div
            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full"
            style={{ background: "#F3EFE7", color: TERRA }}
          >
            <HeartHandshake size={13} /> Why Families Choose Social Pulse
          </div>
          <h2 className="font-display font-black leading-[1.02] mt-5 text-4xl sm:text-5xl">
            Because every family has a story <span style={{ color: TERRA, fontStyle: "italic" }}>worth preserving</span>.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="why-choose-list">
          {WHY_CHOOSE.map((line, i) => (
            <div
              key={i}
              className="rounded-2xl border p-4 flex items-start gap-3 hover:shadow-sm transition-shadow"
              style={{ borderColor: "rgba(0,0,0,0.08)", background: "white" }}
              data-testid={`why-choose-${i}`}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-mono"
                style={{ background: "#F3EFE7", color: TERRA }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm leading-snug" style={{ color: INK }}>{line}</span>
            </div>
          ))}
        </div>

        {/* ============ Email signup ============ */}
        <FamilySignupCard />

        <div
          className="mt-14 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #2D2A26 0%, #3D2E5A 100%)" }}
        >
          <p
            className="font-display italic text-2xl md:text-3xl leading-[1.25] max-w-3xl mx-auto"
            style={{ color: "#F3EFE7" }}
          >
            Because every family has a story worth preserving, and every life deserves to be remembered.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/family"
              data-testid="marketing-bottom-family"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
              style={{ background: TERRA, color: "white" }}
            >
              <TreeDeciduous className="w-4 h-4" /> Start Your Family Tree
            </Link>
            <Link
              to="/celebrate"
              data-testid="marketing-bottom-memorial"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border-2"
              style={{ borderColor: "#F3EFE7", color: "#F3EFE7" }}
            >
              <Flower2 className="w-4 h-4" /> Create a Memorial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const WHY_CHOOSE = [
  "Build beautiful family trees across multiple generations",
  "Create lasting memorials for loved ones",
  "Collaborate with relatives anywhere in the world",
  "Preserve family history, culture, and traditions",
  "Protect priceless memories with secure cloud storage",
  "Discover ancestral roots and reconnect with distant relatives",
  "Share stories, photos, videos, and historical documents",
  "Celebrate birthdays, anniversaries, reunions, and remembrance days",
  "AI-powered biographies and storytelling",
  "Designed specifically for Indian families and the global diaspora",
];


function FeatureCard({ icon, accent, title, body, testid, span = 1 }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 bg-white hover:shadow-md transition-shadow ${span === 2 ? "sm:col-span-2" : ""}`}
      style={{ borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: `${accent}18`, color: accent }}
      >
        {icon}
      </div>
      <div className="mt-4 font-display text-xl leading-tight" style={{ color: "#2D2A26" }}>
        {title}
      </div>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "#5C5852" }}>
        {body}
      </p>
    </div>
  );
}


function MiniRow({ icon, label, color }) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl px-3 py-2 bg-white/70 backdrop-blur"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <span style={{ color }}>{icon}</span>
      <span className="text-xs font-medium" style={{ color: "#2D2A26" }}>{label}</span>
    </div>
  );
}


function SectionDivider({ flip = false }) {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div
        className="h-px w-full"
        style={{
          background: flip
            ? "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.12) 50%, transparent 100%)"
            : "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.12) 50%, transparent 100%)",
        }}
      />
    </div>
  );
}


/**
 * Purely decorative SVG family-tree motif — three generations of nodes
 * connected by clean orthogonal lines. Colours tuned to match the hero
 * palette.
 */
function TreeMotif() {
  const nodes = [
    { x: 130, y: 40, r: 12, color: "#B25D38" },  // ancestor
    { x: 80,  y: 100, r: 10, color: "#5C6B52" },
    { x: 180, y: 100, r: 10, color: "#5C6B52" },
    { x: 40,  y: 160, r: 8,  color: "#B23A4E" },
    { x: 100, y: 160, r: 8,  color: "#B23A4E" },
    { x: 160, y: 160, r: 8,  color: "#B23A4E" },
    { x: 220, y: 160, r: 8,  color: "#B23A4E" },
  ];
  const links = [
    ["0","1"], ["0","2"],
    ["1","3"], ["1","4"],
    ["2","5"], ["2","6"],
  ];
  return (
    <svg viewBox="0 0 260 200" width="100%" height="180" aria-hidden>
      {links.map(([a, b], i) => {
        const A = nodes[+a], B = nodes[+b];
        return (
          <path
            key={i}
            d={`M${A.x} ${A.y + A.r} V${(A.y + B.y) / 2} H${B.x} V${B.y - B.r}`}
            fill="none"
            stroke="#B25D3855"
            strokeWidth="1.5"
          />
        );
      })}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r} fill="white" stroke={n.color} strokeWidth="2" />
          <circle cx={n.x} cy={n.y} r={n.r - 4} fill={n.color} opacity="0.35" />
        </g>
      ))}
    </svg>
  );
}


/* ------------------------------------------------------------------
 * FamilySignupCard
 * Slim horizontal email-capture form. Idempotent server-side so a
 * duplicate submit still returns success — we never leak that the
 * address is already on file.
 * ------------------------------------------------------------------ */
function FamilySignupCard() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setBusy(true);
    try {
      await http.post("/marketing/family-signup", {
        email: email.trim(),
        name: name.trim() || undefined,
        source: "family-and-memorials",
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? "Please enter a valid email address." : (detail || "Could not submit — please try again.");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div
        className="mt-14 rounded-2xl border-2 border-dashed p-6 md:p-8 text-center"
        style={{ borderColor: "#B25D3844", background: "#FDFBF7" }}
        data-testid="family-signup-done"
      >
        <div
          className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3"
          style={{ background: "#065F461A", color: "#065F46" }}
        >
          <Check className="w-6 h-6" />
        </div>
        <div className="font-display text-2xl">You're on the list.</div>
        <p className="text-sm mt-2 opacity-70 max-w-md mx-auto">
          We'll email you thoughtful updates about family-tree tools, memorial features, and stories from other families. No spam, ever.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-14 rounded-2xl border-2 border-dashed p-6 md:p-8"
      style={{ borderColor: "#B25D3844", background: "#FDFBF7" }}
      data-testid="family-signup-card"
    >
      <div className="grid md:grid-cols-[1.2fr_1.5fr] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "#B25D38" }}>
            <Mail className="w-3.5 h-3.5" /> Stay in touch
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mt-2" style={{ color: "#2D2A26" }}>
            Get updates about your family's legacy.
          </h3>
          <p className="text-sm mt-2 opacity-80" style={{ color: "#5C5852" }}>
            Occasional emails with new features, family-history tips, and stories from families building their trees on Social Pulse. Unsubscribe anytime.
          </p>
        </div>
        <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <input
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-full border px-4 py-2.5 text-sm bg-white"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
            data-testid="family-signup-name"
          />
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-full border px-4 py-2.5 text-sm bg-white"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
            data-testid="family-signup-email"
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 shadow-sm hover:shadow transition-shadow"
            style={{ background: "#B25D38", color: "white", opacity: busy ? 0.65 : 1 }}
            data-testid="family-signup-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing you up…</> : "Keep me posted"}
          </button>
        </form>
      </div>
      <div className="text-[11px] mt-3 opacity-60" style={{ color: "#5C5852" }}>
        By subscribing you agree to receive occasional emails from Social Pulse. See our{" "}
        <Link to="/privacy" className="underline">Privacy Policy</Link>.
      </div>
    </div>
  );
}

